import { createFileRoute } from '@tanstack/react-router'
import { Badge } from '@workspace/ui/components/badge'
import { ConfirmButton } from '@workspace/ui/components/confirm-button'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { EmptyState } from '@workspace/ui/components/empty-state'
import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import { FounderLayout } from '../../../../components/founder-layout'
import { useStartupFeatureFlagsQuery } from '../../../../features/feature-flags/hooks/use-startup-feature-flags'
import {
  useDocumentVersionsQuery,
  useEmailUpdatesQuery,
  useFollowersQuery,
  useRemoveEmailUpdate,
  useRemoveFollower,
} from '../../../../features/investor-signals/hooks/use-investor-signals'
import { useCopyToClipboard } from '../../../../hooks/use-copy-to-clipboard'
import { followerDetail } from '../../../../lib/follower-detail'
import { trackEvent } from '../../../../lib/gtag'

export const Route = createFileRoute('/app/startup/$id/updates')({
  component: UpdatesPage,
})

const formatDate = (value: Date | string) =>
  new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

/** Email updates ship with the investor assistant: until it is open, the page says so instead of working. */
function UpdatesPage() {
  const { id: startupId } = Route.useParams()
  const flags = useStartupFeatureFlagsQuery(startupId)
  if (flags.isLoading) {
    return (
      <FounderLayout activeNav="Email updates">
        <div className="flex h-64 items-center justify-center">
          <LoadingSpinner />
        </div>
      </FounderLayout>
    )
  }
  if (!flags.data?.emailUpdates) {
    return (
      <FounderLayout activeNav="Email updates">
        <EmptyState
          title="Not open yet"
          description="Email updates are not open on your account yet."
        />
      </FounderLayout>
    )
  }
  return <UpdatesContent startupId={startupId} />
}

function UpdatesContent({ startupId }: { startupId: string }) {
  const updates = useEmailUpdatesQuery(startupId)
  const removeUpdate = useRemoveEmailUpdate(startupId)
  const followers = useFollowersQuery(startupId)
  const removeFollower = useRemoveFollower(startupId)
  const versions = useDocumentVersionsQuery(startupId)
  const { copied, copy } = useCopyToClipboard()

  const address = updates.data?.data.address ?? null
  const received = (updates.data?.data.updates ?? []).filter((u) => !u.removed)
  const held = updates.data?.data.heldCount ?? 0
  const people = followers.data?.data ?? []
  const earlier = (versions.data?.data ?? []).filter(
    (v) => v.source !== 'EMAIL_UPDATE' && v.supersededAt !== null
  )

  return (
    <FounderLayout activeNav="Email updates">
      <div className="mx-auto max-w-3xl space-y-10 py-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Updates and followers
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Email an update and investors who follow you can hear about it. Followers are shown by
            name, title and firm.
          </p>
        </div>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-foreground">Email an update</h2>
          {address ? (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <code className="rounded-lg border border-border bg-input/50 px-3 py-2 text-sm">
                  {address}
                </code>
                <CtaButton
                  size="sm"
                  onClick={() => {
                    copy(address)
                    trackEvent('email_update_address_copied', { startup_id: startupId })
                  }}
                >
                  {copied ? 'Copied' : 'Copy'}
                </CtaButton>
              </div>
              <p className="text-sm text-muted-foreground">
                Send an update from your own address to this address. Mail from any other address is
                held and never published
                {held > 0 ? ` (${held} held so far)` : ''}.
              </p>
              <div className="space-y-1.5 rounded-xl border border-border p-4">
                <p className="text-sm font-medium text-foreground">Where an update goes</p>
                <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                  <li>It is saved here as a dated update. You can remove it at any time.</li>
                  <li>
                    Investors who follow you with a condition are alerted when it meets what they
                    are waiting for. Investors who follow every major update hear about a hire,
                    funding, a pivot or a milestone. Alerts quote your words, with the date, in
                    their SeedForge app, by email and in their AI assistant.
                  </li>
                  <li>
                    Investors' AI assistants can read it, with its date, when they read your record,
                    check a view of it or search it.
                  </li>
                  <li>
                    It shows on your profile, dated, under Updates. It is not sent to investors who
                    do not follow you, and it does not change your deck.
                  </li>
                </ul>
              </div>
            </>
          ) : null}
          {received.map((update) => (
            <div
              key={update.versionId}
              className="flex items-center justify-between gap-4 rounded-xl border border-border p-4"
            >
              <div>
                <p className="font-medium text-foreground">{update.title}</p>
                <p className="text-xs text-muted-foreground">
                  Received {formatDate(update.receivedAt)}
                </p>
              </div>
              <ConfirmButton
                confirmLabel="Remove?"
                disabled={removeUpdate.isPending}
                onConfirm={() => {
                  trackEvent('email_update_removed', { startup_id: startupId })
                  removeUpdate.mutate({ path: { startupId, versionId: update.versionId } })
                }}
              >
                Remove
              </ConfirmButton>
            </div>
          ))}
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-foreground">Followers</h2>
          {people.length === 0 ? (
            <EmptyState
              title="No followers yet"
              description="Investors who follow you, in the app or through their AI, appear here."
            />
          ) : (
            people.map((person) => (
              <div
                key={person.observerId}
                className="flex items-center justify-between gap-4 rounded-xl border border-border p-4"
              >
                <div>
                  <p className="font-medium text-foreground">{person.name ?? 'An investor'}</p>
                  <p className="text-sm text-muted-foreground">
                    {followerDetail(person) || 'No title or firm'}, since{' '}
                    {formatDate(person.followedAt)}
                  </p>
                </div>
                <ConfirmButton
                  confirmLabel="Remove?"
                  disabled={removeFollower.isPending}
                  onConfirm={() => {
                    trackEvent('follower_removed', { startup_id: startupId })
                    removeFollower.mutate({ path: { startupId, observerId: person.observerId } })
                  }}
                >
                  Remove
                </ConfirmButton>
              </div>
            ))
          )}
        </section>

        {earlier.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-foreground">Earlier versions</h2>
            <p className="text-sm text-muted-foreground">
              Replaced documents are kept with their dates, so followers can see what changed.
            </p>
            {earlier.map((version) => (
              <div
                key={version.id}
                className="flex items-center justify-between rounded-xl border border-border p-4"
              >
                <p className="text-sm text-foreground">
                  {version.label ?? (version.type === 'PITCH_DECK' ? 'Pitch deck' : 'Document')}{' '}
                  <Badge variant="outline">v{version.versionNumber}</Badge>
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatDate(version.uploadedAt)} to{' '}
                  {version.supersededAt ? formatDate(version.supersededAt) : ''}
                </p>
              </div>
            ))}
          </section>
        )}
      </div>
    </FounderLayout>
  )
}
